// src/pages/MasterBarang.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Select,
  Input,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Space,
  Button,
  Modal,
  Form,
  Upload,
  Switch,
  message,
} from "antd";
import {
  FileExcelOutlined,
  PlusOutlined,
  CameraOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "react-medium-image-zoom/dist/styles.css"; // styling safe to keep
import imageCompression from "browser-image-compression";
import { motion, AnimatePresence } from "framer-motion";
import "./MasterBarang.css";
import { getMasterBarang, getGroupBarang, updateBarang } from "../api/api";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_BASE || "";

/* -------------------------
   PinchZoomImage component (unchanged)
   ------------------------- */
function PinchZoomImage({
  src,
  alt,
  fallbackBase64 = null,
  maxScale = 4,
  minScale = 1,
  initialScale = 1,
  className,
  imgStyle = {},
}) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [scale, setScale] = useState(initialScale);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const lastPan = useRef({ x: 0, y: 0 });
  const lastTouch = useRef({});
  const isPointerDown = useRef(false);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const distance = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
  const centerPoint = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

  const reset = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    lastPan.current = { x: 0, y: 0 };
  };

  const clampTranslate = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxOffsetX = Math.max(0, (scale - 1) * rect.width / 2 + 20);
    const maxOffsetY = Math.max(0, (scale - 1) * rect.height / 2 + 20);
    setTranslate((t) => ({ x: clamp(t.x, -maxOffsetX, maxOffsetX), y: clamp(t.y, -maxOffsetY, maxOffsetY) }));
  };

  const handleTouchStart = (e) => {
    if (!e.touches) return;
    if (e.touches.length === 2) {
      const d = distance(e.touches[0], e.touches[1]);
      const c = centerPoint(e.touches[0], e.touches[1]);
      lastTouch.current = { mode: "pinch", startDist: d, startScale: scale, center: c };
    } else if (e.touches.length === 1 && scale > 1) {
      lastTouch.current = {
        mode: "pan",
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        origX: translate.x,
        origY: translate.y,
      };
    } else {
      lastTouch.current = {};
    }
  };

  const handleTouchMove = (e) => {
    if (!e.touches) return;
    if (lastTouch.current.mode === "pinch" && e.touches.length >= 2) {
      const d = distance(e.touches[0], e.touches[1]);
      const scaleDelta = d / lastTouch.current.startDist;
      const newScale = clamp(lastTouch.current.startScale * scaleDelta, minScale, maxScale);

      const c = centerPoint(e.touches[0], e.touches[1]);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const offsetX = c.x - rect.left - rect.width / 2;
        const offsetY = c.y - rect.top - rect.height / 2;
        const factor = (newScale / scale) - 1;
        setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
      }
      setScale(newScale);
    } else if (lastTouch.current.mode === "pan" && e.touches.length === 1) {
      const dx = e.touches[0].clientX - lastTouch.current.startX;
      const dy = e.touches[0].clientY - lastTouch.current.startY;
      setTranslate({ x: lastTouch.current.origX + dx, y: lastTouch.current.origY + dy });
    }
  };

  const handleTouchEnd = () => {
    lastTouch.current = {};
    clampTranslate();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const zoomFactor = delta > 0 ? 1.08 : 0.92;
    const rect = containerRef.current?.getBoundingClientRect();
    const pointer = { x: e.clientX, y: e.clientY };
    if (rect) {
      const offsetX = pointer.x - rect.left - rect.width / 2;
      const offsetY = pointer.y - rect.top - rect.height / 2;
      const newScale = clamp(scale * zoomFactor, minScale, maxScale);
      const factor = (newScale / scale) - 1;
      setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
      setScale(newScale);
    } else {
      setScale((s) => clamp(s * zoomFactor, minScale, maxScale));
    }
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    isPointerDown.current = true;
    lastPan.current = { x: e.clientX, y: e.clientY, origX: translate.x, origY: translate.y };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isPointerDown.current) return;
    const dx = e.clientX - lastPan.current.x;
    const dy = e.clientY - lastPan.current.y;
    setTranslate({ x: lastPan.current.origX + dx, y: lastPan.current.origY + dy });
  };

  const handleMouseUp = () => {
    isPointerDown.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
    clampTranslate();
  };

  const handleDoubleClick = (e) => {
    if (scale === 1) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const offsetX = e.clientX - rect.left - rect.width / 2;
        const offsetY = e.clientY - rect.top - rect.height / 2;
        const targetScale = Math.min(2, maxScale);
        const factor = (targetScale / scale) - 1;
        setTranslate((t) => ({ x: t.x - offsetX * factor, y: t.y - offsetY * factor }));
        setScale(targetScale);
      } else {
        setScale(2);
      }
    } else {
      reset();
    }
  };

  const handleImgError = (e) => {
    try {
      const el = e.currentTarget;
      if (fallbackBase64 && el.src !== `data:image/jpeg;base64,${fallbackBase64}`) {
        el.onerror = null;
        el.src = `data:image/jpeg;base64,${fallbackBase64}`;
      } else {
        el.onerror = null;
        el.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
      }
    } catch (err) {/* silent */}
  };

  const zoomIn = (step = 0.25) => setScale((s) => clamp(s + step, minScale, maxScale));
  const zoomOut = (step = 0.25) => setScale((s) => clamp(s - step, minScale, maxScale));

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

useEffect(() => {
  try {
    if (containerRef.current) {
      containerRef.current._pz = { zoomIn, zoomOut, reset, getState: () => ({ scale, translate }) };
    }
  } catch (e) { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [scale, translate]);


  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
        touchAction: "none",
      }}
      className={className}
    >
      <img
        ref={imgRef}
        src={src || ""}
        alt={alt || ""}
        onError={handleImgError}
        draggable={false}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          display: "block",
          transformOrigin: "center center",
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: "transform 0.02s linear",
          willChange: "transform",
          ...imgStyle,
        }}
      />
      <div style={{ display: "none" }} data-zoom-state={JSON.stringify({ scale, translate })} />
    </div>
  );
}

/* -------------------------
   Main component: MasterBarang
   ------------------------- */
const MasterBarang = ({ propAccess = [] }) => {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(() => localStorage.getItem("selectedGroup") || null);
  const [selectedCategory, setSelectedCategory] = useState(() => localStorage.getItem("selectedCategory") || null);
  const [barangList, setBarangList] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("searchTerm") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [lastUpdatedKode, setLastUpdatedKode] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();
  const usersession = user?.name || "SYSTEM";
  const [userAccess] = useState(
    propAccess.length > 0 ? propAccess : Array.isArray(user?.access) ? user.access.map(Number) : []
  );

  const getBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const cacheBustedUrl = (url) => {
    if (!url) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}_=${Date.now()}`;
  };

  const resolveImageSrc = (b, { size = "thumb", format = "webp" } = {}) => {
    if (!b) return null;
    // if imageUrl is a data URI or absolute url, return it
    if (b.imageUrl) {
      if (/^(https?:|data:)/.test(b.imageUrl)) return b.imageUrl;
      if (API_BASE) return `${API_BASE}${b.imageUrl}`;
      if (b.imageUrl.startsWith("/")) return `${window.location.origin}${b.imageUrl}`;
      return b.imageUrl;
    }
    // prefer embedded base64 if available (fast)
    if (b.gambarBase64) return `data:image/jpeg;base64,${b.gambarBase64}`;
    if (b.gambar && b.gambar.startsWith("data:")) return b.gambar;
    // fallback to constructed image endpoint (with cache-buster)
    if (b.kd_brg) {
      const constructed = `${API_BASE}/images/${encodeURIComponent(b.kd_brg)}?size=${size}&format=${format}`;
      return cacheBustedUrl(constructed);
    }
    return null;
  };

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getGroupBarang();
        if (res.status === "ok" && Array.isArray(res.data)) setGroups(res.data);
        else setError(res.message || "Data grup tidak valid");
      } catch (err) {
        console.error("Error fetch groups:", err);
        setError("Gagal mengambil data grup");
      }
    };
    fetchGroups();
  }, []);

  // LOAD BARANG: dipanggil setiap kali selectedGroup berubah
 useEffect(() => {
  let cancelled = false;

  const doLoad = async (grp) => {
    if (!grp) {
      setBarangList([]);
      setCategories([]);
      setError(null);
      setLoading(false);
      return;
    }

    setCategories([]);
    setError(null);
    setLoading(true);

    try {
      await loadBarang(grp);
    } catch (err) {
      if (!cancelled) {
        console.error("Error loading barang (useEffect):", err);
        setError("Gagal mengambil data barang");
        setBarangList([]);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  doLoad(selectedGroup);

  return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedGroup]);


  // loadBarang: prefer base64 if server returned it; otherwise build image URL
  const loadBarang = async (group) => {
    if (!group) return;
    setError(null);
    setLoading(true);
    try {
      const res = await getMasterBarang(group);
      if (res.status === "ok" && Array.isArray(res.data)) {
        const barangWithImages = res.data.map((b) => {
          const base64Only = b.gambar || null; // API may return plain base64 without data: prefix
          const imageUrlFromApi = b.imageUrl || null;

          // Normalize imageUrlFromApi: if relative (startsWith "/") prefix API_BASE
          let imageUrlCandidate = null;
          if (imageUrlFromApi) {
            if (/^(https?:)?\/\//.test(imageUrlFromApi)) {
              imageUrlCandidate = imageUrlFromApi;
            } else if (API_BASE) {
              imageUrlCandidate = imageUrlFromApi.startsWith("/") ? `${API_BASE}${imageUrlFromApi}` : `${API_BASE}/${imageUrlFromApi}`;
            } else {
              imageUrlCandidate = imageUrlFromApi;
            }
          }

          // priority:
          // 1) embedded base64 from API (fast & reliable)
          // 2) explicit imageUrl returned by API
          // 3) constructed images endpoint (cache-busted)
          const constructedEndpoint = b.kd_brg ? cacheBustedUrl(`${API_BASE}/images/${encodeURIComponent(b.kd_brg)}?size=thumb&format=webp`) : null;

          const finalImageUrl = base64Only
            ? `data:image/jpeg;base64,${base64Only}`
            : (imageUrlCandidate || constructedEndpoint || null);

          return {
            ...b,
            gambarBase64: base64Only,
            gambar: base64Only, // legacy alias
            imageUrl: finalImageUrl,
          };
        });

        setBarangList(barangWithImages);
        const uniqueCategories = [...new Set(res.data.map((b) => b.kategory_brg))];
        setCategories(uniqueCategories);
      } else {
        setBarangList([]);
        setCategories([]);
      }
    } catch (err) {
      console.error("❌ Error mengambil data barang:", err);
      setError("Gagal mengambil data barang");
      setBarangList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    selectedGroup ? localStorage.setItem("selectedGroup", selectedGroup) : localStorage.removeItem("selectedGroup");
  }, [selectedGroup]);

  useEffect(() => {
    selectedCategory ? localStorage.setItem("selectedCategory", selectedCategory) : localStorage.removeItem("selectedCategory");
  }, [selectedCategory]);

  useEffect(() => {
    localStorage.setItem("searchTerm", searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const container = document.querySelector(".master-container");
    if (!container) return;
    let lastY = 0;
    let ticking = false;
    const handleScroll = () => {
      const currentY = container.scrollTop;
      setIsScrolled(currentY > 5);
      setShowScrollTop(currentY > 250);
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (currentY > lastY + 10 && currentY > 100) setShowFilter(false);
          else if (currentY < lastY - 10) setShowFilter(true);
          lastY = currentY <= 0 ? 0 : currentY;
          ticking = false;
        });
        ticking = true;
      }
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!lastUpdatedKode) return;
    const el = document.getElementById(`barang-${lastUpdatedKode}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("highlight-card");
    const timeoutId = setTimeout(() => {
      el.classList.remove("highlight-card");
      setLastUpdatedKode(null);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [barangList, lastUpdatedKode]);

  const filteredBarang = barangList.filter((b) => {
    const matchSearch =
      b.nm_barang?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.kd_brg?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = selectedCategory ? b.kategory_brg === selectedCategory : true;
    return matchSearch && matchCategory;
  });

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("MASTER BARANG");
      ws.columns = [
        { header: "Kode Barang", key: "kd_brg", width: 20 },
        { header: "Nama Barang", key: "nm_barang", width: 35 },
        { header: "Grup", key: "grup_brg", width: 20 },
        { header: "Kategori", key: "kategory_brg", width: 20 },
        { header: "Fungsi/Tindakan", key: "val_3", width: 50 },
        { header: "Status", key: "sts_brg", width: 15 },
        { header: "Gambar", key: "gambar", width: 20 },
      ];
      for (let b of filteredBarang) {
        const rowNumber = ws.addRow({
          kd_brg: b.kd_brg,
          nm_barang: b.nm_barang,
          grup_brg: b.grup_brg,
          kategory_brg: b.kategory_brg,
          val_3: b.val_3,
          sts_brg: b.sts_brg,
        }).number;
        ws.getRow(rowNumber).height = 60;
        ws.getRow(rowNumber).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
        const base64Str = b.gambarBase64 || b.gambar || null;
        if (base64Str) {
          const base64Data = base64Str.replace(/^data:image\/\w+;base64,/, "");
          const imageBuffer = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
          const imageId = workbook.addImage({ buffer: imageBuffer, extension: "jpeg" });
          ws.addImage(imageId, { tl: { col: 6, row: rowNumber - 1 }, ext: { width: 160, height: 105 } });
        }
      }
      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `MasterBarang_${selectedGroup || "All"}.xlsx`);
      message.success("Export Excel berhasil ✅");
    } catch (error) {
      console.error("❌ Error export Excel:", error);
      message.error("Gagal mengekspor data ke Excel");
    } finally {
      setExporting(false);
    }
  };

  const openForm = (barang) => {
    if (!userAccess.includes(1128)) {
      message.warning("Anda tidak memiliki akses untuk update barang ❌");
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      kd_brg: barang.kd_brg,
      nm_barang: barang.nm_barang,
      kategory_brg: barang.kategory_brg,
      val_3: barang.val_3,
      status: barang.sts_brg === "AKTIF",
    });

    const previewSrc = (barang.imageUrl && (barang.imageUrl.startsWith("http") ? barang.imageUrl : `${API_BASE}${barang.imageUrl}`)) ||
      (barang.gambarBase64 ? `data:image/jpeg;base64,${barang.gambarBase64}` : null);

    setPhotoPreview(previewSrc);
    setFormVisible(true);
  };

  const handleFormSubmit = async (values) => {
    if (!values.kd_brg || !values.nm_barang) return;
    setSaving(true);
    let compressedImage = photoPreview;
    try {
      if (photoPreview && photoPreview.startsWith("data:image")) {
        const file = await fetch(photoPreview).then((r) => r.blob());
        const options = { maxSizeMB: 0.05, useWebWorker: true };
        const compressedBlob = await imageCompression(file, options);
        compressedImage = await getBase64(compressedBlob); // data URL
      }

      let gambarForServer = null;
      if (compressedImage) {
        gambarForServer = compressedImage.includes(",") ? compressedImage.split(",")[1] : compressedImage;
      }

      const payload = {
        kd_brg: values.kd_brg,
        nm_barang: values.nm_barang,
        val_3: values.val_3 || "",
        sts_brg: values.status ? "AKTIF" : "TIDAK AKTIF",
        val_2: usersession,
        gambar: gambarForServer,
      };

      const res = await updateBarang(payload);
      if (res.status === "ok") {
        // interpret server response if available (imageBase64 or imageUrl)
        const serverImageUrlRaw = res.data?.imageUrl || null;
        const serverImageBase64Raw = res.data?.imageBase64 || null;
        const dataUrlLocal = compressedImage && compressedImage.startsWith("data:") ? compressedImage : null;
        const base64OnlyLocal = dataUrlLocal ? dataUrlLocal.split(",")[1] : (gambarForServer || null);

        let finalImageSrc = null;
        let finalBase64Only = null;

        if (serverImageBase64Raw) {
          if (serverImageBase64Raw.startsWith("data:")) {
            finalImageSrc = serverImageBase64Raw;
            finalBase64Only = serverImageBase64Raw.split(",")[1];
          } else {
            finalImageSrc = `data:image/jpeg;base64,${serverImageBase64Raw}`;
            finalBase64Only = serverImageBase64Raw;
          }
        } else if (serverImageUrlRaw) {
          if (/^(https?:)?\/\//.test(serverImageUrlRaw)) {
            finalImageSrc = serverImageUrlRaw;
          } else if (API_BASE) {
            finalImageSrc = serverImageUrlRaw.startsWith("/") ? `${API_BASE}${serverImageUrlRaw}` : `${API_BASE}/${serverImageUrlRaw}`;
          } else finalImageSrc = serverImageUrlRaw;
        } else if (dataUrlLocal) {
          finalImageSrc = dataUrlLocal;
          finalBase64Only = base64OnlyLocal;
        } else {
          finalImageSrc = `${API_BASE}/images/${encodeURIComponent(payload.kd_brg)}?size=full&format=webp&_=${Date.now()}`;
        }

        // Update local state so UI shows immediately
        setBarangList(prev => prev.map(b =>
          b.kd_brg === payload.kd_brg ? {
            ...b,
            nm_barang: payload.nm_barang,
            val_3: payload.val_3,
            sts_brg: payload.sts_brg,
            imageUrl: finalImageSrc || b.imageUrl,
            gambarBase64: finalBase64Only || b.gambarBase64,
            gambar: finalBase64Only || b.gambar,
          } : b
        ));

        setFormVisible(false);
        message.success("Barang berhasil diperbarui ✅");
        setLastUpdatedKode(payload.kd_brg);

        // Option: if you want guaranteed canonical state, uncomment:
        // await loadBarang(selectedGroup);
      } else {
        message.error(res.message || "Gagal update barang ❌");
      }
    } catch (err) {
      console.error("❌ Error handleFormSubmit:", err);
      message.error("Terjadi kesalahan saat menyimpan data.");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = (barang) => {
    const rawApi = barang.imageUrl
      ? (barang.imageUrl.startsWith("http") ? barang.imageUrl.replace(/size=thumb/, "size=full") : `${API_BASE}${barang.imageUrl.replace(/size=thumb/, "size=full")}`)
      : `${API_BASE}/images/${encodeURIComponent(barang.kd_brg)}?size=full&format=webp`;

    const apiImageUrl = cacheBustedUrl(rawApi);
    const gambarBase64 = barang.gambarBase64 || barang.gambar || null;
    const fullImageUrl = apiImageUrl || (gambarBase64 ? `data:image/jpeg;base64,${gambarBase64}` : null);

    setPreviewData({ ...barang, fullImageUrl, gambarBase64 });
    setPreviewVisible(true);
    previewContainerRef.current = null;
  };

  const openCamera = () => {
    if (!/Android|iPhone|iPad/i.test(navigator.userAgent)) {
      message.info("Fitur kamera hanya tersedia di perangkat mobile 📱");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleCameraChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await getBase64(file);
      const base64Only = dataUrl.split(",")[1];
      setPhotoPreview(dataUrl);
      form.setFieldsValue({ gambar: [{ originFileObj: file }] });

      const kdBrg = form.getFieldValue("kd_brg");
      setBarangList((prev) => prev.map((b) =>
        b.kd_brg === kdBrg ? { ...b, gambarBase64: base64Only, gambar: base64Only, imageUrl: dataUrl } : b
      ));
    } catch (err) {
      console.error("Error camera change:", err);
      message.error("Gagal membaca file gambar.");
    }
  };

  const callPreviewZoom = (cmd) => {
    try {
      const stored = previewContainerRef.current;
      if (stored && stored._pz) {
        if (cmd === "in") stored._pz.zoomIn();
        else if (cmd === "out") stored._pz.zoomOut();
        else if (cmd === "reset") stored._pz.reset();
        return;
      }
      const c = document.querySelector(".ant-modal-root .ant-modal-content .pinch-zoom-container");
      if (c && c._pz) {
        if (cmd === "in") c._pz.zoomIn();
        else if (cmd === "out") c._pz.zoomOut();
        else if (cmd === "reset") c._pz.reset();
      }
    } catch (e) { /* fail silently */ }
  };

  const scrollToTop = () => {
    const container = document.querySelector(".master-container");
    if (container) container.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="master-container fade-in">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleCameraChange}
      />
      <Title level={3}>MASTER BARANG</Title>

      <div className={`filter-bar ${isScrolled ? "scrolled" : ""} ${showFilter ? "show" : "hide"}`}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Pilih Grup Barang"
              value={selectedGroup}
              onChange={(val) => {
                // reset UI quickly, then load via effect
                setSelectedCategory(null);
                setSearchTerm("");
                setBarangList([]); // clear old items while new group loads
                setSelectedGroup(val);
              }}
              style={{ width: "100%" }}
              allowClear
            >
              {groups.map((g) => (
                <Option key={g} value={g}>{g}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Kategori"
              value={selectedCategory}
              onChange={setSelectedCategory}
              allowClear
              disabled={!categories.length}
              style={{ width: "100%" }}
            >
              {categories.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Input.Search
              placeholder="Cari kode/nama barang..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              disabled={!selectedGroup}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                disabled={filteredBarang.length === 0}
              >
                Export Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {loading && (
        <div className="center"><Spin size="large" /></div>
      )}

      {error && !loading && <Alert type="error" message="Error" description={error} showIcon />}

      {!loading && !selectedGroup && <Empty description="Pilih grup dahulu" />}
      {!loading && selectedGroup && filteredBarang.length === 0 && <Empty description="Tidak ada data barang" />}

      {selectedGroup && (
        <Row gutter={[16, 16]} justify="start" className="barang-grid fade-in">
          {filteredBarang.length > 0 ? filteredBarang.map((b) => {
            const imgSrc = resolveImageSrc(b);
            return (
              <Col id={`barang-${b.kd_brg}`} xs={12} sm={8} md={6} lg={4} key={b.kd_brg}>
                <Card
                  hoverable
                  onClick={() => openForm(b)}
                  className="barang-card"
                  cover={
                    <div className="barang-cover" onClick={(e) => { e.stopPropagation(); openPreview(b); }}>
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={b.nm_barang || "Barang"}
                          style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 6 }}
                          onError={(e) => {
                            try {
                              const fb = b.gambarBase64 ? `data:image/jpeg;base64,${b.gambarBase64}` : null;
                              if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
                              else if (!e.currentTarget.src || !e.currentTarget.src.startsWith("data:")) e.currentTarget.src = "data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
                            } catch (err) { /* silent */ }
                          }}
                          loading="lazy"
                        />
                      ) : <div className="no-image">No Image</div>}
                    </div>
                  }
                >
                  <Card.Meta
                    title={<div className="asset-title">{b.nm_barang || "-"}</div>}
                    description={
                      <div className="asset-details">
                        <div><strong>Kode:</strong> {b.kd_brg || "-"}</div>
                        <div><strong>Grup:</strong> {b.grup_brg || "-"}</div>
                        <div><strong>Kategori:</strong> {b.kategory_brg || "-"}</div>
                        <div className={`status ${b.sts_brg === "AKTIF" ? "aktif" : "nonaktif"}`}>{b.sts_brg || "-"}</div>
                      </div>
                    }
                  />
                </Card>
              </Col>
            );
          }) : <Col span={24}><Empty description="Tidak ada data barang" /></Col>}
        </Row>
      )}

      {/* Modal Update */}
      <Modal
        open={formVisible}
        title="Update Barang"
        onCancel={() => setFormVisible(false)}
        onOk={() => form.submit()}
        okText="Simpan"
        destroyOnHidden
        centered
        width={window.innerWidth < 768 ? "95%" : 600}
        styles={{ body: { maxHeight: window.innerWidth < 768 ? "70vh" : "60vh", overflowY: "auto", padding: "1rem" } }}
        style={{ top: window.innerWidth < 768 ? 10 : 50 }}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item label="Kode Barang" name="kd_brg"><Input disabled /></Form.Item>
          <Form.Item label="Nama Barang" name="nm_barang" rules={[{ required: true, message: "Nama barang wajib diisi" }]}><Input /></Form.Item>
          <Form.Item label="Kategori Barang" name="kategory_brg"><Input disabled /></Form.Item>
          <Form.Item label="Fungsi/Tindakan" name="val_3"><TextArea rows={window.innerWidth < 768 ? 3 : 4} /></Form.Item>
          <Form.Item label="Status" name="status" valuePropName="checked"><Switch checkedChildren="AKTIF" unCheckedChildren="TIDAK AKTIF" /></Form.Item>

          <Form.Item label="Gambar">
            <Form.Item name="gambar" valuePropName="fileList" getValueFromEvent={(e) => e && e.fileList} noStyle>
              <Upload
                listType="picture"
                maxCount={1}
                beforeUpload={() => false}
                accept="image/*"
                onChange={async (info) => {
                  const file = info.fileList[0]?.originFileObj;
                  if (file) {
                    try {
                      const dataUrl = await getBase64(file);
                      const base64Only = dataUrl.split(",")[1];
                      setPhotoPreview(dataUrl);
                      const kdBrg = form.getFieldValue("kd_brg");
                      setBarangList((prev) => prev.map((b) => b.kd_brg === kdBrg ? { ...b, gambarBase64: base64Only, gambar: base64Only, imageUrl: dataUrl } : b));
                    } catch (err) {
                      console.error("Upload onChange error:", err);
                      message.error("Gagal membaca file gambar");
                    }
                  } else {
                    setPhotoPreview(null);
                  }
                }}
              >
                <Button icon={<PlusOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>

            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Button icon={<CameraOutlined />} onClick={openCamera}>Kamera</Button>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" style={{ marginTop: 10, width: 160, borderRadius: 8, objectFit: "contain", maxHeight: 120 }} />
              ) : (
                <div style={{ color: "#aaa", border: "1px dashed #ccc", borderRadius: 8, padding: 8 }}>Tidak ada gambar</div>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Preview */}
      <Modal
        open={previewVisible}
        title={previewData?.nm_barang || "Preview"}
        onCancel={() => setPreviewVisible(false)}
        footer={<Button onClick={() => setPreviewVisible(false)}>Tutup</Button>}
        centered
        width={window.innerWidth < 768 ? "90%" : 600}
        styles={{ body: { padding: "1rem", maxHeight: "80vh", overflowY: "auto" } }}
        style={{ top: window.innerWidth < 768 ? 10 : 50 }}
      >
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 30, display: "flex", gap: 8 }}>
            <Button size="small" onClick={() => callPreviewZoom("out")}>−</Button>
            <Button size="small" onClick={() => callPreviewZoom("in")}>+</Button>
            <Button size="small" onClick={() => callPreviewZoom("reset")}>Reset</Button>
          </div>

          <div style={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {previewData?.fullImageUrl || previewData?.gambarBase64 ? (
              <div className="pinch-zoom-container" ref={(el) => { if (el) previewContainerRef.current = el; }}>
                <PinchZoomImage
                  src={previewData?.fullImageUrl || (previewData?.gambarBase64 ? `data:image/jpeg;base64,${previewData.gambarBase64}` : "")}
                  alt={previewData?.nm_barang || "Preview"}
                  fallbackBase64={previewData?.gambarBase64}
                  maxScale={4}
                  minScale={1}
                  imgStyle={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 480 }}
                />
              </div>
            ) : <Empty description="Tidak ada gambar" />}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <strong>Fungsi/Tindakan:</strong>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{previewData?.val_3 || "-"}</div>
        </div>
      </Modal>

      {saving && (<div className="overlay-loading"><Spin size="large" tip="Menyimpan data..." /></div>)}

      <AnimatePresence>{showScrollTop && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.25 }}
          onClick={scrollToTop}
          style={{ position: "fixed", bottom: 30, right: 30, background: "#1890ff", color: "#fff", borderRadius: "50%", padding: 12, cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.3)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          whileHover={{ scale: 1.08 }}>
          <ArrowUpOutlined style={{ fontSize: 18 }} />
        </motion.div>
      )}</AnimatePresence>

      {exporting && (<div className="overlay-loading"><Spin size="large" tip="Mengekspor ke Excel..." /></div>)}
    </div>
  );
};

export default MasterBarang;
